import { RestError } from '@azure/core-http';
import {
  BatchCommittedDto,
  CompleteCourierRegistrationDto,
  CourierApprovalDto,
  CourierBatchImportDto,
  CourierBatchPrepareDto,
  CourierBatchPreparedDto,
  CourierCapabilitiesDto,
  CourierDto,
  CreateCourierDto,
  EditCourierDto,
  KnownEditUserDtoStatus,
  KnownSortOrder,
  MoveCourierDto,
  UserDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import {
  COURIERS_PAGE_SIZE,
  CouriersListQueryInput,
  makeCouriersListQueryParams
} from './list/logic/couriers-list-query.map';

const COURIER_EXPORT_PAGE_SIZE = 100;

/**
 * A single courier. Unlike `usersController.get`, this answers with the courier's carrier group and
 * project in full, plus the fields that only mean something for a courier — the login method their
 * carrier sets, the username and access code that go with it, and their courier-app access level.
 * Unlike the list, this always returns the access code — it is the screen the code is relayed from.
 */
export function useQueryGetCourier(courierID?: string) {
  const api = useApi();

  return useQuery<CourierDto | undefined, RestError>(
    [QueryKeys.COURIERS, courierID],
    async () => {
      if (!courierID) {
        return undefined;
      }
      return await api.couriersController.get(courierID);
    },
    { enabled: !!courierID }
  );
}

/**
 * `GET /admin/couriers/:id/capabilities` — what the courier can actually do at a kiosk, which the
 * courier record cannot answer: the permissions come from their carrier's network grants, not from
 * anything stored on the courier.
 *
 * Separate from `useQueryGetCourier` rather than folded into it, so the header still renders when
 * this fails. A courier whose capabilities cannot be read is not a courier who cannot be viewed.
 */
export function useQueryGetCourierCapabilities(courierID?: string) {
  const api = useApi();

  return useQuery<CourierCapabilitiesDto | undefined, RestError>(
    [QueryKeys.COURIERS, courierID, QueryKeys.CAPABILITIES],
    async () => {
      if (!courierID) {
        return undefined;
      }
      return await api.couriersController.getCapabilities(courierID);
    },
    { enabled: !!courierID }
  );
}

/**
 * `GET /admin/couriers` rather than the users list narrowed to couriers — the rows are `CourierDto`,
 * carrying the carrier, login method and access-code flag that `UserDto` has no room for. Child
 * projects are left out: a courier is reached through their carrier, not the project tree.
 *
 * `includeAccessCode` is deliberately not passed: the list would then carry every courier's code,
 * and the rows only need `hasAccessCode`. The details screen is where a code is read.
 */
export function useInfiniteQueryCouriersList(projectID: string | undefined, params: CouriersListQueryInput) {
  const api = useApi();

  const queryParams = makeCouriersListQueryParams(params);

  return useInfiniteQuery(
    [QueryKeys.COURIERS, projectID, params],
    async ({ pageParam = 1 }) => {
      if (!projectID) {
        return { items: [], totalRecords: 0, perPageRecords: COURIERS_PAGE_SIZE, totalPages: 1, pageNo: 1 };
      }

      const result = await api.couriersController.list({
        ...queryParams,
        projectID,
        includeChildProjects: false,
        perPageRecords: COURIERS_PAGE_SIZE,
        pageNo: pageParam
      });

      return { ...result, items: result.items || [] };
    },
    {
      enabled: !!projectID,
      getNextPageParam(lastPage) {
        return lastPage.pageNo < lastPage.totalPages ? lastPage.pageNo + 1 : undefined;
      }
    }
  );
}

export function useMutationCreateCourier() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CourierDto, RestError, CreateCourierDto>(
    async (dto) => {
      return await api.couriersController.create(dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
      }
    }
  );
}

/**
 * `PATCH /admin/couriers/:id` rather than the generic user edit, so the courier-specific rules
 * apply — most importantly the check that an edit cannot leave a courier without the credential
 * their carrier's login method needs, which comes back as a 400 for the form to surface.
 *
 * A courier is also cached as a user (the details page reads them through `usersController.get`),
 * so both namespaces are invalidated — unlike create, which brings no user record with it.
 */
export function useMutationEditCourier(courierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CourierDto, RestError, EditCourierDto>(
    async (dto) => {
      return await api.couriersController.edit(courierID, dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

/**
 * `PATCH /admin/couriers/:id/approval` — sets the access level and status together, writes AC's
 * approval flag and leaves a note. The general edit refuses a rejection because it would skip both.
 *
 * Invalidates users as well as couriers: a courier is cached as both.
 */
export function useMutationSetCourierApproval(courierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CourierDto, RestError, CourierApprovalDto>(
    async (dto) => {
      return await api.couriersController.setApproval(courierID, dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

/**
 * `POST /admin/couriers/:id/move` — the only way a courier changes carrier; neither edit endpoint does it.
 * Couriers are cached as users too, and a move shifts the courier counts either side of it.
 */
export function useMutationMoveCourier(courierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CourierDto, RestError, MoveCourierDto>(
    async (dto) => {
      return await api.couriersController.move(courierID, dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}

/** The courier joins the carrier as pending. */
export function useMutationCompleteCourierRegistration(courierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CourierDto, RestError, CompleteCourierRegistrationDto>(
    async (dto) => {
      return await api.couriersController.completeRegistration(courierID, dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}

/** Uses the users endpoint, as the couriers one refuses incomplete couriers. */
export function useMutationDeactivateIncompleteCourier(courierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<UserDto, RestError, void>(
    async () => {
      return await api.usersController.edit(courierID, { status: KnownEditUserDtoStatus.Inactive });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationListAllCarrierCouriers() {
  const api = useApi();

  return useMutation<CourierDto[], RestError, { groupID: string }>(async ({ groupID }) => {
    const couriers: CourierDto[] = [];
    let pageNo = 1;
    let totalPages = 1;

    do {
      const page = await api.couriersController.list({
        groupID,
        includeChildGroups: true,
        includeAccessCode: true,
        sortBy: 'firstName',
        sortOrder: KnownSortOrder.Asc,
        perPageRecords: COURIER_EXPORT_PAGE_SIZE,
        pageNo
      });
      couriers.push(...(page.items ?? []));
      totalPages = page.totalPages;
      pageNo++;
    } while (pageNo <= totalPages);

    return couriers;
  });
}

export function useMutationPrepareCourierBatch() {
  const api = useApi();

  return useMutation<CourierBatchPreparedDto, RestError, CourierBatchPrepareDto>(async (dto) => {
    return await api.couriersBatchController.prepare(dto);
  });
}

export function useMutationImportCourierBatch() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<BatchCommittedDto, RestError, CourierBatchImportDto>(
    async (dto) => {
      return await api.couriersBatchController.import(dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.COURIERS]);
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

/**
 * Notes on the courier, off their own controller rather than the users one, and with the notes on
 * their deliveries and orders alongside — as a user's notes are.
 */
export function useInfiniteQueryCourierNotesList(courierID: string, sortOrder?: KnownSortOrder) {
  const api = useApi();

  return useInfiniteQuery(
    [QueryKeys.COURIERS, courierID, QueryKeys.NOTES, sortOrder],
    async ({ pageParam = 1 }) => {
      return await api.couriersNotesController.findAll(courierID, {
        sortOrder,
        includeNotesOnRelatedResources: true,
        perPageRecords: 5,
        pageNo: pageParam
      });
    },
    {
      getNextPageParam(lastPage) {
        return lastPage.pageNo < lastPage.totalPages ? lastPage.pageNo + 1 : undefined;
      }
    }
  );
}
